import { EVENT_TYPE } from '@atlaskit/editor-common/analytics/types/enums';
import type { AnalyticsEventPayload } from '@atlaskit/editor-common/analytics/types/events';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types/next-editor-plugin';
import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';
import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';
import { ExperienceName } from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import { getActiveRemixCreateId } from '@atlassian/conversation-assistant-instrumentation/controllers/experience-tracker/remix-create-collect';
import { ExperienceTrackerHandler } from '@atlassian/conversation-assistant-instrumentation/main';

import { replaceMauiApp } from '../editor-commands/replace-maui-app';
import type {
	MalleableUiPlugin,
	PluginConfiguration,
	ReplaceMauiAppProps,
} from '../malleableUiPluginType';
import { malleableUiPluginKey } from '../pm-plugins/plugin-key';

type DuplicateAnalyticsPayload = {
	action: 'succeeded' | 'failed';
	actionSubject: 'mauiAppDuplicate';
	actionSubjectId: 'mauiInlineEdit';
	attributes: Record<string, unknown>;
	eventType: EVENT_TYPE;
};

/**
 * Result of {@link DuplicateAndReplaceMauiApp}. Carries the id that
 * actually ended up on the document node — i.e. the duplicated id when
 * the `cc-maui-phase-2` duplication path ran, or the original BE-provided
 * `newMauiId` when duplication was skipped or fell back.
 *
 * Consumers (e.g. the rovo-bridge MAUI update processor) need this so they
 * can record stack entries whose URL matches the live node, not the
 * pre-duplication BE id.
 */
export type DuplicateAndReplaceMauiAppResult = {
	/** The MAUI app id that ended up on the document node after replacement. */
	insertedMauiId: string;
};

export type DuplicateAndReplaceMauiAppProps = ReplaceMauiAppProps & {
	requireInsertionAcknowledgement?: boolean;
};

export type DuplicateAndReplaceMauiApp = (
	props: DuplicateAndReplaceMauiAppProps,
) => Promise<DuplicateAndReplaceMauiAppResult | undefined>;

/**
 * Builds the duplicate-and-replace action used when Rovo replaces a MAUI app.
 *
 * Convo-AI returns a system-space app id; we duplicate under the current page so it inherits page perms before swapping the extension URL.
 */
export function makeDuplicateAndReplaceMauiApp({
	api,
	config,
}: {
	api: ExtractInjectionAPI<MalleableUiPlugin> | undefined;
	config: PluginConfiguration | undefined;
}): DuplicateAndReplaceMauiApp {
	return async ({ previousMauiId, newMauiId, localId, requireInsertionAcknowledgement }) => {
		let idToInsert = newMauiId;

		if (
			!FeatureGates.getExperimentValue<boolean>(
				'remix_maui_duplicate_stability_improvement',
				'value',
				false,
			)
		) {
			// TODO: lift these deps out of `config.diagramPaste` to top-level `PluginConfiguration` once both flows share a config surface.
			const duplicateConfig = config?.diagramPaste;
			const duplicateMauiApp = duplicateConfig?.duplicateMauiApp;
			const containerContentId = duplicateConfig?.getCurrentPageId?.();
			const containerContentType = duplicateConfig?.getCurrentContentType?.();

			if (duplicateMauiApp && containerContentId) {
				const experienceTracker = ExperienceTrackerHandler.getInstance().experienceTracker;
				const experienceAttributes = {
					appId: newMauiId,
					currentPageId: containerContentId,
					source: 'mauiInlineEdit',
				};

				experienceTracker.start({
					name: ExperienceName.REMIX_DUPLICATE_APP,
					id: ExperienceName.REMIX_DUPLICATE_APP,
					attributes: { ...experienceAttributes, remixCreateId: getActiveRemixCreateId() },
				});

				try {
					const { id, attempts, statusCode } = await duplicateMauiApp(
						newMauiId,
						containerContentId,
						containerContentType,
					);
					idToInsert = id;
					const wasNoOp = id === newMauiId;

					fireAnalytics(api, {
						eventType: EVENT_TYPE.OPERATIONAL,
						actionSubject: 'mauiAppDuplicate',
						action: 'succeeded',
						actionSubjectId: 'mauiInlineEdit',
						attributes: {
							originalId: newMauiId,
							newId: id,
							attempts,
							statusCode,
							wasNoOp,
						},
					});

					experienceTracker.succeed({
						name: ExperienceName.REMIX_DUPLICATE_APP,
						attributes: {
							...experienceAttributes,
							newAppId: id,
							attempts,
							statusCode,
							wasNoOp,
						},
					});
				} catch (error) {
					const statusCode = (error as { statusCode?: number } | null)?.statusCode;
					const errorCode = (error as { code?: string } | null)?.code;
					const attempts = (error as { attempts?: 1 | 2 } | null)?.attempts;
					const isPermissionError = statusCode === 403;
					fireAnalytics(api, {
						eventType: EVENT_TYPE.OPERATIONAL,
						actionSubject: 'mauiAppDuplicate',
						action: 'failed',
						actionSubjectId: 'mauiInlineEdit',
						attributes: {
							originalId: newMauiId,
							errorMessage: (error as Error)?.message,
							isPermissionError,
							statusCode,
							errorCode,
							attempts,
						},
					});

					const duplicateError =
						error instanceof Error ? error : new Error('Failed to duplicate MAUI app');
					experienceTracker.fail({
						teamName: 'CC CE MAU',
						name: ExperienceName.REMIX_DUPLICATE_APP,
						error: duplicateError,
						attributes: {
							...experienceAttributes,
							statusCode,
							errorCode,
							isPermissionError,
							attempts,
							remixCreateId: getActiveRemixCreateId(),
						},
					});
					experienceTracker.fail({
						teamName: 'CC CE MAU',
						name: ExperienceName.REMIX_INSERT,
						error: duplicateError,
						attributes: {
							...experienceAttributes,
							failedDueTo: ExperienceName.REMIX_DUPLICATE_APP,
							statusCode,
							errorCode,
							isPermissionError,
							attempts,
							remixCreateId: getActiveRemixCreateId(),
						},
					});
				}
			}
		}

		const aiDecorationState =
			api?.aiExperience?.sharedState.currentState()?.aiDecorationExperiencePluginState;
		const isMatchingInlineEditTarget =
			Boolean(previousMauiId) &&
			Boolean(localId) &&
			((Boolean(aiDecorationState?.isInlineEditReady) &&
				aiDecorationState?.inlineEditReadyTargetId === localId) ||
				aiDecorationState?.inlineRovoStreamingTargetId === localId);
		const shouldManageInlineEditScroll =
			isMatchingInlineEditTarget && isExperimentEnabled('cc_maui_polish_changes_batch_6');
		const isTargetInView = shouldManageInlineEditScroll
			? api?.aiExperience?.actions.isInlineEditTargetInView?.(localId)
			: undefined;
		const suppressScrollIntoView = shouldManageInlineEditScroll && isTargetInView !== true;

		const didReplace = api?.core?.actions.execute(({ tr }) => {
			const result = replaceMauiApp({
				previousMauiId,
				newMauiId: idToInsert,
				localId,
				...(suppressScrollIntoView ? { suppressScrollIntoView } : {}),
			})({
				tr,
			});

			if (result && localId) {
				tr.setMeta(malleableUiPluginKey, { addAiEditReplacementLocalId: localId });
			}

			return result;
		});

		if (!didReplace && requireInsertionAcknowledgement) {
			return undefined;
		}

		return { insertedMauiId: idToInsert };
	};
}

function fireAnalytics(
	api: ExtractInjectionAPI<MalleableUiPlugin> | undefined,
	payload: DuplicateAnalyticsPayload,
) {
	api?.analytics?.actions.fireAnalyticsEvent(payload as unknown as AnalyticsEventPayload);
}
